import { useState } from "react";
import { Tag, Plus, Pencil, Trash2, Save, X, ScanText, ChevronRight } from "lucide-react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface Brand {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export default function BrandsManagement() {
  const { t } = useTranslation("admin");
  const { isSuperadmin: isAdmin, isLoading } = useIsSuperadmin();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Brand | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: brands, isLoading: brandsLoading } = useQuery({
    queryKey: ["brands"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brands")
        .select("*")
        .order("name", { ascending: true });
      if (error) throw error;
      return data as Brand[];
    },
  });

  const upsertMutation = useMutation({
    mutationFn: async (input: { id?: string; name: string; description: string | null }) => {
      if (input.id) {
        const { error } = await supabase
          .from("brands")
          .update({ name: input.name, description: input.description })
          .eq("id", input.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("brands")
          .insert({ name: input.name, description: input.description });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      toast({ title: editing ? "Brand updated" : "Brand created" });
      closeDialog();
    },
    onError: (err: any) => {
      toast({
        title: "Error saving brand",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("brands").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      toast({ title: "Brand deleted" });
      setDeleteId(null);
    },
    onError: (err: any) => {
      toast({
        title: "Error deleting brand",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      });
    },
  });

  const openCreate = () => {
    setEditing(null);
    setName("");
    setDescription("");
    setDialogOpen(true);
  };

  const openEdit = (brand: Brand) => {
    setEditing(brand);
    setName(brand.name);
    setDescription(brand.description ?? "");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setName("");
    setDescription("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    upsertMutation.mutate({
      id: editing?.id,
      name: name.trim(),
      description: description.trim() || null,
    });
  };

  if (isLoading || brandsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Access enforced by the route guard in App.tsx.

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-primary" />
            <h1 className="text-2xl font-bold text-foreground">{t("brands.title")}</h1>
          </div>
          <Button onClick={openCreate} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Brand
          </Button>
        </div>
        <p className="text-muted-foreground">
          Manage appliance brands available for brand-specific workflows
        </p>
      </div>

      <div className="px-6">
        <button
          onClick={() => navigate("/platform/brands/decoding")}
          className="w-full mb-3 bg-card rounded-xl border border-border p-5 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <ScanText className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-foreground">{t("settings.sections.decoding.title")}</p>
            <p className="text-sm text-muted-foreground">{t("settings.sections.decoding.description")}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>

        <button
          onClick={() => navigate("/platform/brands/decoding-coverage")}
          className="w-full mb-4 bg-card rounded-xl border border-border p-5 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <ScanText className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-foreground">{t("decoderCoverage.tile.title")}</p>
            <p className="text-sm text-muted-foreground">{t("decoderCoverage.tile.description")}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>

        <div className="bg-card rounded-xl border border-border overflow-hidden">
          {brands && brands.length > 0 ? (
            <ul className="divide-y divide-border">
              {brands.map((brand) => (
                <li
                  key={brand.id}
                  className="flex items-center justify-between p-4 hover:bg-muted/40 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground truncate">{brand.name}</p>
                    {brand.description && (
                      <p className="text-sm text-muted-foreground truncate">
                        {brand.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(brand)}
                      aria-label={`Edit ${brand.name}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteId(brand.id)}
                      className="text-destructive hover:text-destructive"
                      aria-label={`Delete ${brand.name}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              No brands yet. Click "Add Brand" to create one.
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => (open ? setDialogOpen(true) : closeDialog())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Brand" : "Add Brand"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="brand-name">Name</Label>
              <Input
                id="brand-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Bosch"
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="brand-description">Description (optional)</Label>
              <Input
                id="brand-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Short note about this brand"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog} className="gap-2">
                <X className="w-4 h-4" />
                Cancel
              </Button>
              <Button type="submit" disabled={upsertMutation.isPending || !name.trim()} className="gap-2">
                <Save className="w-4 h-4" />
                {editing ? "Save Changes" : "Create Brand"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete brand?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will permanently remove the brand. Workflows referencing this brand by name will keep their stored value, but it will no longer appear in selection lists.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
              className="gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
